import { Injectable, effect, inject, signal } from '@angular/core';

import { AppSettings } from '../app-settings';
import { GeocodeResult } from '../interfaces/geocode-response.interface';
import { CountrySettings } from '../interfaces/app-settings.interface';
import { OrderStore } from '../order-store';
import { ApiService } from './api.service';

export type GeolocateStatus = 'loading' | 'success' | 'error';

export interface GeoResult {
  status: 'success' | 'error';
  type?: 'geolocation' | 'geocoding';
  error?: number;
  payload?: {
    city: string;
    location: { latitude: number; longitude: number };
    address: string;
  };
}

@Injectable({ providedIn: 'root' })
export class MapService {
  private readonly apiService = inject(ApiService);
  private readonly store = inject(OrderStore);

  appSettings: CountrySettings = AppSettings.settings['default'];
  subsidiary = 'default';
  countryPlaceId = this.appSettings.countryPlaceId;
  enabled = true;
  center: string | { lat: number; lng: number } | undefined;

  private readonly geolocateUserSignal = signal<GeolocateStatus | null>(null);
  private readonly changeCenterSignal = signal<string | null>(null);

  readonly geolocateUser = this.geolocateUserSignal.asReadonly();
  readonly changeCenter = this.changeCenterSignal.asReadonly();

  constructor() {
    effect(() => {
      const order = this.store.order();
      if (!order) {
        return;
      }
      if (AppSettings.settings[order.country]) {
        this.appSettings = AppSettings.settings[order.country];
      }
      this.subsidiary = order.country;
      this.countryPlaceId = this.appSettings.countryPlaceId;
    });
  }

  public ready(): void {
    // subsidiary is kept in sync reactively via the constructor effect
  }

  private getCenter() {
    return this.center;
  }

  private setCenter(latLng: string): void {
    this.changeCenterSignal.set(latLng);
    this.center = latLng;
  }

  private getCountryPlaceId(placesDetails: GeocodeResult[]): string {
    let placeId = '';
    placesDetails.forEach((v) => {
      if (v.types.indexOf('country') !== -1) {
        placeId = v.place_id;
      }
    });
    return placeId;
  }

  public geocode(lat: number, lng: number): Promise<GeoResult> {
    return new Promise((resolve) => {
      this.apiService.getGeodedInfo(lat, lng).subscribe((data) => {
        let response: GeoResult;
        if (data['status'] === 'OK') {
          const result = data['results'][0];
          if (this.getCountryPlaceId(data['results']) === this.countryPlaceId) {
            response = {
              status: 'success',
              payload: {
                city: result.address_components[
                  result.address_components.length - 2
                ].long_name,
                location: {
                  latitude: lat,
                  longitude: lng,
                },
                address: result['formatted_address'],
              },
            };
          } else {
            response = { status: 'error', error: 0, type: 'geocoding' };
          }
        } else {
          response = { status: 'error', error: 1, type: 'geocoding' };
        }
        resolve(response);
      });
    });
  }

  public getCurrentPosition(): Promise<GeoResult> {
    this.geolocateUserSignal.set('loading');
    return new Promise((resolve) => {
      const geoOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 60000,
        maximumAge: 0,
      };

      navigator.geolocation.getCurrentPosition(
        (data) => {
          const coords = {
            lat: data.coords.latitude,
            lng: data.coords.longitude,
          };
          const location = `${coords.lat},${coords.lng}`;
          this.setCenter(location);
          this.geocode(data.coords.latitude, data.coords.longitude).then(
            (geocoded) => {
              this.geolocateUserSignal.set(
                geocoded.status === 'success' ? 'success' : 'error',
              );
              resolve(geocoded);
            },
          );
        },
        (err) => {
          this.geolocateUserSignal.set('error');
          resolve({ status: 'error', error: err.code, type: 'geolocation' });
        },
        geoOptions,
      );
    });
  }
}
